import { Action, ActionMap } from "@reactables/core";
import { CustomReducers } from "@reactables/forms";
import { of } from "rxjs";
import { switchMap, map, catchError } from "rxjs/operators";
import { ContactType } from "../../../../../Constants/contactTypes";
import { Observable, from } from "rxjs";
import { NormalizedCreditCard } from "../../../Models/payment.model";
import { sameAddress } from "./sameAddress.reducer";
import { contactType } from "./contactType.reducer";
import { Token } from "Features/CoverageApplication/Models/applicationForm.model";
import {
  MicroFormFieldChangeEvent,
  Tokenizer,
} from "Features/CoverageApplication/Components/CheckoutPage/PaymentInformation/useMicroform";

export interface PaymentActions extends ActionMap {
  selectSameAddressAsContact: (payload: "yes" | "no") => void;
  selectDifferentPaymentAddressContactType: (payload: ContactType) => void;
  onMicroformFieldChange: (payload: {
    controlName: string;
    event: MicroFormFieldChangeEvent;
  }) => void;
  onMicroformFieldBlur: (controlName: string) => void;
  confirmCreditCard: (payload: {
    normalizedCC: NormalizedCreditCard;
    tokenizer: Tokenizer;
  }) => void;
  creditCardReset: () => void;
}

export const paymentReducers: CustomReducers<PaymentActions> = {
  selectSameAddressAsContact: sameAddress(
    ["payment", "billing"],
    "sameAddressAsContact",
  ),
  selectDifferentPaymentAddressContactType: contactType([
    "payment",
    "billing",
    "differentAddressFields",
  ]),
  onMicroformFieldChange: (
    { updateValues },
    state,
    {
      payload: { controlName, event },
    }: Action<{ controlName: string; event: MicroFormFieldChangeEvent }>,
  ) => {
    return updateValues(state, {
      controlRef: ["payment", "billing", "creditCard", controlName],
      value: event,
    });
  },
  onMicroformFieldBlur: (
    { markControlAsTouched },
    state,
    { payload: controlName }: Action<string>,
  ) => {
    return markControlAsTouched(state, {
      controlRef: ["payment", "billing", "creditCard", controlName],
    });
  },
  /**
   * @description obtains a cybersource token for payment
   */
  confirmCreditCard: {
    reducer: ({ updateValues }, state) =>
      updateValues(state, {
        controlRef: ["payment", "token"],
        value: "pending",
      }),
    effects: [
      (
        action$: Observable<
          Action<{
            normalizedCC: NormalizedCreditCard;
            tokenizer: Tokenizer;
          }>
        >,
      ) =>
        action$.pipe(
          switchMap(
            ({
              payload: {
                normalizedCC: { cardExpirationMonth, cardExpirationYear },
                tokenizer,
              },
            }) => {
              return from(
                tokenizer({
                  expirationMonth: cardExpirationMonth,
                  expirationYear: cardExpirationYear,
                }),
              ).pipe(
                map((token) => ({
                  type: "confirmCreditCardSuccess",
                  payload: token,
                })),
                catchError((e) =>
                  of({ type: "confirmCreditCardFailure", payload: e }),
                ),
              );
            },
          ),
        ),
    ],
  },
  confirmCreditCardSuccess: (
    { updateValues },
    state,
    { payload }: Action<Token>,
  ) => {
    state = updateValues(state, {
      controlRef: ["payment", "confirmed"],
      value: true,
    });

    state = updateValues(state, {
      controlRef: ["payment", "token"],
      value: payload,
    });

    return state;
  },
  confirmCreditCardFailure: ({ updateValues }, state, action) => {
    return updateValues(state, {
      controlRef: ["payment", "token"],
      value: action.payload,
    });
  },
  creditCardReset: ({ resetControl }, state) =>
    resetControl(state, ["payment", "billing", "creditCard"]),
};
